import React, { useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { Divider, Modal, Table, Tag, Typography } from 'antd';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import moment from 'moment';
import axios from 'axios';

import { useFormatMessage } from '@/hooks';
import { log } from 'utils';
import { url } from 'utils/url';
import { regexInput } from 'utils/regex';

import ModalScreenForm from 'components/ModalScreenForm';
import ModalDeleteConfirmation from 'components/ModalDeleteConfirmation';
import ModalMediaCategoryForm from 'components/ModalMediaCategoryForm';
import ModalScreenSchedulingForm from 'components/ModalScreenSchedulingForm';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import ModalFolderForm from 'components/ModalFolderForm';
import FolderTree from 'components/FolderTree';
// eslint-disable-next-line no-unused-vars
import FolderBreadcrumb from 'components/FolderBreadcrumb';
import CategoryDropdown from 'components/CategoryDropdown';
import DropdownWrapper from 'components/DropdownWrapper';
import TimelineSequence from 'components/TimelineSequence';
import ScreenTitleIcon from 'assets/icon/dynamic/ScreenTitleIcon';
import { ForwardIcon } from 'assets/icon';

import {
  bulkCopyScreens,
  bulkDeleteScreens,
  checkScreenConnection,
  createScreen,
  deleteScreen,
  doOta,
  downloadScreenConfig,
  fetchScreenPreview,
  fetchScreens,
  generateApiKey,
  getDeviceLogs,
  getLatestAndroidBundle,
  modifyScreen,
  moveScreen,
  revokeApiKey,
  setActiveScreen,
} from 'state/actions/screens';
import {
  bulkCopyFolders,
  bulkDeleteFolders,
  deleteFolder,
  fetchFolders,
  moveFolder,
  updateFolder,
} from 'state/actions/folders';
import { bulkUpdate, bulkVolume } from 'state/actions/settings';
import {
  createScreenGroup,
  deleteScreenGroup,
  fetchScreenGroups,
  modifyScreenGroup,
  setScreenGroup,
} from 'state/actions/screenGroup';
import {
  createScreenScheduling,
  deleteScreenScheduling,
  fetchScreenScheduling,
  modifyScreenScheduling,
  setScreenScheduling,
} from 'state/actions/screenScheduling';
import { clearUsersData } from 'state/actions/users';

import './Screens.scss';

const { Text } = Typography;

const DEVICE_STATUS_META = {
  online: { label: 'Online', color: '#16a34a' },
  offline: { label: 'Offline', color: '#dc2626' },
  checking: { label: 'Checking...', color: '#d97706' },
  unknown: { label: 'Unknown', color: '#9ca3af' },
};

const LOG_STATUS_COLORS = {
  success: 'green',
  failed: 'red',
  warning: 'orange',
  info: 'blue',
};

const LOG_EVENT_COLORS = {
  OTA_START: 'geekblue',
  OTA_SUCCESS: 'green',
  OTA_FAILED: 'red',
  PING: 'cyan',
  REGISTER: 'purple',
  RESTART: 'volcano',
  MEDIA_UPDATE: 'blue',
  VOLUME: 'gold',
};

const normalizeAppVersion = (value) =>
  String(value || '')
    .trim()
    .replace(/^v/i, '');

// The device log is the only record of what a screen actually installed, so the
// displayed app version comes from the newest successful INSTALL/OTA entry
// rather than from whatever version we last pushed.
const getLatestInstalledAppVersion = (logs = []) => {
  const logsWithVersion = logs.filter((item) =>
    normalizeAppVersion(item?.appVersion)
  );

  const successfulInstallLogs = logsWithVersion.filter((item) => {
    const event = String(item?.event || '').toUpperCase();
    return (
      item?.status === 'success' &&
      (event === 'INSTALL' || event === 'OTA_SUCCESS')
    );
  });

  const candidates = successfulInstallLogs.length
    ? successfulInstallLogs
    : logsWithVersion;

  const latestLog = [...candidates].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  )[0];

  return normalizeAppVersion(latestLog?.appVersion);
};

export default function Screens({ history }) {
  const { siteId, siteName } = useParams();
  const dispatch = useDispatch();

  const {
    error,
    loading,
    loadingScreenGroup,
    loadingScreenScheduling,
    deleted,
    updated,
    screensList,
    foldersList,
    screenGroupList,
    screenSchedulingList,
    screenScheduling,
    userData,
    role,
    screenGroup,
  } = useSelector(
    (state) => ({
      error: state.screens.error,
      loading: state.screens.loading,
      loadingScreenGroup: state.screengroup.loading,
      loadingScreenScheduling: state.screenScheduling.loading,
      screenGroupList: state.screengroup.list,
      screenSchedulingList: state.screenScheduling.list,
      screenScheduling: state.screenScheduling.scheduling,
      deleted: state.screens.deleted,
      updated: state.screens.updated,
      screensList: state.screens.list,
      foldersList: state.folders.list,
      userData: state.auth.userData,
      role: state.auth.userData.role,
      screenGroup: state.screengroup.screenGroup,
    }),
    shallowEqual
  );

  const uid = userData.id;

  const defaultScreen = {
    name: '',
    description: '',
    modifiedAt: moment(Date.now()).format('DD-MM-YY hh:mm:ss'),
    modifiedBy: userData.email ? userData.email : '',
  };

  // --- screen CRUD -------------------------------------------------------
  const [deleteModal, setDeleteModal] = useState({
    screenId: null,
    isOpen: false,
  });
  const [visible, setVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [screen, setScreen] = useState(defaultScreen);
  const [apikeyModal, setApikeyModal] = useState({
    screen: null,
    isOpen: false,
  });

  // --- search / filters --------------------------------------------------
  const [search, setSearch] = useState('');
  const [searchError, setSearchError] = useState(null);
  const [selectedAppVersion, setSelectedAppVersion] = useState('all');

  // --- screen groups -----------------------------------------------------
  const [isModalShow, setIsModalShow] = useState(false);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [deleteGroup, setDeleteGroup] = useState();
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [tempSelectedGroup, setTempSelectedGroup] = useState('all');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // --- screen scheduling -------------------------------------------------
  const [selectedScreenScheduling, setSelectedScreenScheduling] =
    useState('all');
  const [tempSelectedScreenScheduling, setTempSelectedScreenScheduling] =
    useState('all');
  const [isModalSchedulingShow, setIsModalSchedulingShow] = useState(false);
  const [deletedScreenScheduling, setDeletedScreenScheduling] = useState(null);
  const [
    isDeleteScreenSchedulingModalShow,
    setIsDeleteScreenSchedulingModalShow,
  ] = useState(false);
  const [schedulingDropdownOpen, setSchedulingDropdownOpen] = useState(false);

  // --- folder explorer ---------------------------------------------------
  const [currentFolder, setCurrentFolder] = useState(null);
  const [isFolderModalVisible, setIsFolderModalVisible] = useState(false);
  const [isEditingFolder, setIsEditingFolder] = useState(false);
  const [currentFolderEdit, setCurrentFolderEdit] = useState({});
  const [deleteFolderModal, setDeleteFolderModal] = useState({
    folder: null,
    isOpen: false,
  });

  // --- selection + bulk actions -----------------------------------------
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [selectedScreens, setSelectedScreens] = useState([]);
  const [bulkDeleteModal, setBulkDeleteModal] = useState({
    type: null,
    items: [],
    isOpen: false,
  });
  const [bulkVolumeModal, setBulkVolumeModal] = useState({
    isOpen: false,
    volume: 50,
  });

  // --- drag and drop -----------------------------------------------------
  const [activeId, setActiveId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);

  // --- inline rename -----------------------------------------------------
  const [renamingItem, setRenamingItem] = useState(null);

  // --- device state ------------------------------------------------------
  const [deviceStatuses, setDeviceStatuses] = useState({});
  const [checkingScreenIds, setCheckingScreenIds] = useState([]);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const otaRecheckTimerRef = useRef(null);
  const [deviceLogModal, setDeviceLogModal] = useState(false);
  const [selectedScreenId, setSelectedScreenId] = useState(null);
  const [deviceLogs, setDeviceLogs] = useState([]);
  const [deviceLogLoading, setDeviceLogLoading] = useState(false);
  const [deviceLastInstalledVersions, setDeviceLastInstalledVersions] =
    useState({});
  const [appVersionFilterLoading, setAppVersionFilterLoading] = useState(false);
  const appVersionRequestIdRef = useRef(0);

  // --- OTA ---------------------------------------------------------------
  const [otaModal, setOtaModal] = useState({ isOpen: false, screenIds: [] });
  const [otaFile, setOtaFile] = useState(null);
  const [otaVersion, setOtaVersion] = useState('');
  const [otaDescription, setOtaDescription] = useState('');
  const [otaUploading, setOtaUploading] = useState(false);
  const [otaBundles, setOtaBundles] = useState([]);
  const [otaBundlesLoading, setOtaBundlesLoading] = useState(false);
  const [selectedBundleId, setSelectedBundleId] = useState('');
  const [latestBundle, setLatestBundle] = useState(null);
  const [latestBundleLoading, setLatestBundleLoading] = useState(false);

  // --- preview -----------------------------------------------------------
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewScreen, setPreviewScreen] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  // An 8px threshold keeps a click on a checkbox or action button from being
  // read as the start of a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchOtaBundles = async () => {
    try {
      setOtaBundlesLoading(true);

      const response = await axios.get(`${url}/api/ota/bundles`, {
        params: { platform: 'android' },
      });

      const rawBundles = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];

      setOtaBundles(
        rawBundles
          .map((item) => ({ ...item, id: String(item.id || item._id || '') }))
          .filter((item) => item.id)
      );
    } catch (err) {
      console.error('Failed to load OTA bundles:', err);
      setOtaBundles([]);
    } finally {
      setOtaBundlesLoading(false);
    }
  };

  const fetchLatestBundle = async () => {
    try {
      setLatestBundleLoading(true);
      const res = await getLatestAndroidBundle();
      setLatestBundle(res?.data || null);
    } catch (err) {
      console.error(err);
      setLatestBundle(null);
    } finally {
      setLatestBundleLoading(false);
    }
  };

  const fetchDeviceLogsFor = async (screenId) => {
    try {
      setDeviceLogLoading(true);
      const res = await getDeviceLogs({ screenId });
      if (res?.success) setDeviceLogs(res.data || []);
    } catch (err) {
      console.error('Failed to load device logs', err);
    } finally {
      setDeviceLogLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchScreens({ siteId }));
    dispatch(fetchFolders({ siteId }));
    dispatch(fetchScreenGroups({ siteId }));
    dispatch(fetchScreenScheduling({ siteId }));
    fetchOtaBundles();
    return () => dispatch(clearUsersData());
  }, [dispatch, role, siteId]); // eslint-disable-line

  useEffect(() => {
    if (deleted && !loading) {
      setDeleteModal({ screenId: null, isOpen: false });
      setSelectedScreens([]);
      dispatch(fetchScreens({ siteId }));
    }
  }, [deleted, loading]); // eslint-disable-line

  useEffect(() => {
    if (updated && !loading) {
      dispatch(fetchScreens({ siteId }));
      setVisible(false);
    }
  }, [updated, loading]); // eslint-disable-line

  // ---------------------------------------------------------------------
  // Screen group handlers
  // ---------------------------------------------------------------------
  const handleAddCategory = () => {
    dispatch(setScreenGroup({ name: '', description: '' }));
    setIsModalShow(true);
  };

  const handleEditCategory = (id) => {
    dispatch(setScreenGroup(screenGroupList.find((cat) => cat._id === id)));
    setIsModalShow(true);
  };

  const handleDeleteCategory = (item) => {
    setDeleteGroup(item);
    setIsDeleteModal(true);
  };

  const getCategory = () =>
    screenGroupList.find((cat) => cat._id === selectedGroup)?.name;

  const onCreateScreenGroup = () => {
    const action = screenGroup._id ? modifyScreenGroup : createScreenGroup;
    dispatch(
      action(
        { uid, ...screenGroup, siteId, id: screenGroup._id },
        setIsModalShow
      )
    );
  };

  const handleSetPlaylist = () => {
    setSelectedGroup(tempSelectedGroup);
    setDropdownOpen(false);
  };

  const handleDeleteScreenGroup = () => {
    dispatch(
      deleteScreenGroup({ id: deleteGroup._id, siteId }, () =>
        setIsDeleteModal(false)
      )
    );
    setSelectedGroup('all');
  };

  const handleCancelDeleteCategory = () => {
    setDeleteGroup(null);
    setIsDeleteModal(false);
  };

  // ---------------------------------------------------------------------
  // Screen scheduling handlers
  // ---------------------------------------------------------------------
  const handleAddScreenScheduling = () => {
    dispatch(
      setScreenScheduling({
        name: '',
        description: '',
        screenId: '',
        activeConfigIndices: [],
      })
    );
    setIsModalSchedulingShow(true);
  };

  const handleEditScreenScheduling = (id) => {
    dispatch(
      setScreenScheduling(screenSchedulingList.find((cat) => cat._id === id))
    );
    setIsModalSchedulingShow(true);
  };

  const handleDeleteScreenScheduling = (item) => {
    setDeletedScreenScheduling(item);
    setIsDeleteScreenSchedulingModalShow(true);
  };

  const getScreenScheduling = () =>
    screenSchedulingList.find((cat) => cat._id === selectedScreenScheduling)
      ?.name;

  const handleSetScreenScheduling = () => {
    setSelectedScreenScheduling(tempSelectedScreenScheduling);
    setSchedulingDropdownOpen(false);
  };

  const handleOkDeleteScreenScheduling = () => {
    dispatch(
      deleteScreenScheduling({ id: deletedScreenScheduling._id, siteId }, () =>
        setIsDeleteScreenSchedulingModalShow(false)
      )
    );
    setSelectedScreenScheduling('all');
  };

  const handleCancelDeleteScreenScheduling = () => {
    setDeletedScreenScheduling(null);
    setIsDeleteScreenSchedulingModalShow(false);
  };

  const onCreateScreenScheduling = (finalScheduling) => {
    const action = screenScheduling._id
      ? modifyScreenScheduling
      : createScreenScheduling;

    dispatch(
      action(
        {
          ...screenScheduling,
          ...finalScheduling,
          siteId,
          id: screenScheduling._id,
        },
        setIsModalSchedulingShow
      )
    );
  };

  // ---------------------------------------------------------------------
  // Screen handlers
  // ---------------------------------------------------------------------
  const onCreate = (values) => {
    const action = isEditing ? modifyScreen : createScreen;
    log(values, '<<<< values screen oncreate');
    dispatch(action(values));
  };

  const onNewScreenHandler = () => {
    // New screens land in whichever folder the explorer is showing.
    setScreen({ ...defaultScreen, parentId: currentFolder });
    setIsEditing(false);
    setVisible(true);
  };

  const onRemoveButtonClickHandler = (screenId) => {
    setDeleteModal({ screenId, isOpen: true });
  };

  const onCloseModalHandler = () => {
    setDeleteModal({ screenId: null, isOpen: false });
  };

  const onDeleteScreenHandler = () => {
    dispatch(deleteScreen({ id: deleteModal.screenId, siteId }));
  };

  const onCloseModalApikeyHandler = () => {
    setApikeyModal({ screen: null, isOpen: false });
  };

  const handleSearch = (e) => {
    setSearchError(null);
    if (!regexInput(e.target.value) && e.target.value) {
      setSearchError('Search cannot using special character');
    }
    setSearch(e.target.value);
  };

  const handleGoTo = (item) => {
    dispatch(
      setActiveScreen(item, () =>
        history.push(`/${siteId}/screen/${siteName}/${item._id}`)
      )
    );
  };

  const handlePreviewScreen = async (item) => {
    try {
      setPreviewScreen(item);
      setIsPreviewModalOpen(true);
      setPreviewData(
        await dispatch(fetchScreenPreview({ screenId: item._id }))
      );
    } catch (err) {
      console.error('Failed to fetch screen preview:', err);
      setPreviewData(null);
    }
  };

  const closePreviewModal = () => {
    setIsPreviewModalOpen(false);
    setPreviewScreen(null);
    setPreviewData(null);
  };

  // ---------------------------------------------------------------------
  // Folder handlers
  // ---------------------------------------------------------------------
  const handleFolderNavigate = (folderId) => {
    setCurrentFolder(folderId);
    setSelectedFolders([]);
    setSelectedScreens([]);
  };

  const handleCreateFolder = () => {
    setCurrentFolderEdit({ parentId: currentFolder });
    setIsEditingFolder(false);
    setIsFolderModalVisible(true);
  };

  const handleEditFolder = (folder) => {
    setCurrentFolderEdit(folder);
    setIsEditingFolder(true);
    setIsFolderModalVisible(true);
  };

  const onCloseFolderModal = () => {
    setIsFolderModalVisible(false);
    setCurrentFolderEdit({});
    setIsEditingFolder(false);
  };

  const onDeleteFolderConfirm = () => {
    dispatch(deleteFolder({ id: deleteFolderModal.folder._id, siteId }));
    setDeleteFolderModal({ folder: null, isOpen: false });
  };

  // ---------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------
  // parentId comes back populated (an object) from some endpoints and as a raw
  // id from others, so every comparison goes through this.
  const parentIdOf = (item) => item?.parentId?._id || item?.parentId || null;

  const appVersionOptions = useMemo(
    () =>
      Array.from(
        new Set(
          otaBundles
            .map((bundle) => normalizeAppVersion(bundle?.version))
            .filter(Boolean)
        )
      ).sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      ),
    [otaBundles]
  );

  useEffect(() => {
    const stillExists =
      selectedAppVersion === 'all' ||
      appVersionOptions.includes(selectedAppVersion);

    if (!stillExists) setSelectedAppVersion('all');
  }, [appVersionOptions, selectedAppVersion]);

  const { folders: currentFolders, screens: currentScreens } = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    const subfolders = foldersList.filter(
      (folder) => parentIdOf(folder) === currentFolder
    );

    const screensHere = screensList.filter((item) => {
      if (parentIdOf(item) !== currentFolder) return false;

      if (selectedGroup !== 'all' && item.groupId !== selectedGroup) {
        return false;
      }

      if (
        selectedScreenScheduling !== 'all' &&
        !(item.schedulingIds || []).some(
          (s) => (s?._id || s) === selectedScreenScheduling
        )
      ) {
        return false;
      }

      return true;
    });

    return {
      folders: subfolders.filter(
        (folder) =>
          !normalizedSearch ||
          folder.name.toLowerCase().includes(normalizedSearch)
      ),
      screens: screensHere.filter((item) => {
        const matchesSearch =
          !normalizedSearch ||
          item.name.toLowerCase().includes(normalizedSearch);
        const matchesAppVersion =
          selectedAppVersion === 'all' ||
          deviceLastInstalledVersions[String(item._id)] === selectedAppVersion;

        return matchesSearch && matchesAppVersion;
      }),
    };
  }, [
    foldersList,
    screensList,
    currentFolder,
    search,
    selectedGroup,
    selectedScreenScheduling,
    selectedAppVersion,
    deviceLastInstalledVersions,
  ]);

  const screenIds = useMemo(
    () =>
      screensList
        .filter((item) => parentIdOf(item) === currentFolder)
        .map((item) => item._id),
    [screensList, currentFolder]
  );
  const screenIdsKey = screenIds.join(',');

  // One device-log request per screen in the folder. The ref guards against a
  // slow batch from a previous folder landing after the current one.
  useEffect(() => {
    const requestId = ++appVersionRequestIdRef.current;

    if (screenIds.length === 0) {
      setDeviceLastInstalledVersions({});
      setAppVersionFilterLoading(false);
      return;
    }

    setAppVersionFilterLoading(true);

    Promise.all(
      screenIds.map(async (id) => {
        try {
          const response = await getDeviceLogs({ screenId: id });
          const logs = Array.isArray(response?.data) ? response.data : [];
          return [String(id), getLatestInstalledAppVersion(logs)];
        } catch (err) {
          console.error(`Failed to read installed version for ${id}:`, err);
          return [String(id), ''];
        }
      })
    )
      .then((results) => {
        if (appVersionRequestIdRef.current !== requestId) return;
        setDeviceLastInstalledVersions(Object.fromEntries(results));
      })
      .finally(() => {
        if (appVersionRequestIdRef.current === requestId) {
          setAppVersionFilterLoading(false);
        }
      });
  }, [screenIdsKey]); // eslint-disable-line

  const handleCheckScreensConnection = async (ids) => {
    const targetIds = (ids || []).map(String).filter(Boolean);
    if (targetIds.length === 0) return;

    setCheckingScreenIds((prev) => Array.from(new Set([...prev, ...targetIds])));

    try {
      const result = await dispatch(
        checkScreenConnection({ screenIds: targetIds })
      );
      const onlineIds = (result?.online || []).map(String);
      const offlineIds = (result?.offline || []).map(String);
      const answered = new Set([...onlineIds, ...offlineIds]);

      setDeviceStatuses((prev) => {
        const next = { ...prev };
        onlineIds.forEach((id) => {
          next[id] = 'online';
        });
        offlineIds.forEach((id) => {
          next[id] = 'offline';
        });
        targetIds.forEach((id) => {
          if (!answered.has(id)) next[id] = 'unknown';
        });
        return next;
      });

      const reportedVersions = (result?.details || []).reduce((acc, item) => {
        const version = normalizeAppVersion(item?.appVersion);
        if (item?.screenId && version) acc[String(item.screenId)] = version;
        return acc;
      }, {});

      if (Object.keys(reportedVersions).length > 0) {
        setDeviceLastInstalledVersions((prev) => ({
          ...prev,
          ...reportedVersions,
        }));
      }

      setLastCheckedAt(result?.checkedAt || new Date().toISOString());
    } finally {
      setCheckingScreenIds((prev) =>
        prev.filter((id) => !targetIds.includes(id))
      );
    }
  };

  useEffect(() => {
    if (screenIds.length > 0) handleCheckScreensConnection(screenIds);
  }, [screenIdsKey]); // eslint-disable-line

  useEffect(
    () => () => {
      if (otaRecheckTimerRef.current) clearTimeout(otaRecheckTimerRef.current);
    },
    []
  );

  const getDeviceStatus = (id) => {
    const key = String(id);
    if (checkingScreenIds.includes(key)) return 'checking';
    return deviceStatuses[key] || 'unknown';
  };

  const connectionSummary = useMemo(() => {
    const ids = currentScreens.map((item) => String(item._id));
    return {
      online: ids.filter((id) => deviceStatuses[id] === 'online').length,
      offline: ids.filter((id) => deviceStatuses[id] === 'offline').length,
      total: ids.length,
    };
  }, [currentScreens, deviceStatuses]);

  const openDeviceLogs = (screenId) => {
    setSelectedScreenId(screenId);
    setDeviceLogModal(true);
    fetchDeviceLogsFor(screenId);
  };

  // ---------------------------------------------------------------------
  // Selection + bulk actions
  // ---------------------------------------------------------------------
  const handleFolderSelect = (folderId, isSelected) => {
    setSelectedFolders((prev) =>
      isSelected ? [...prev, folderId] : prev.filter((id) => id !== folderId)
    );
  };

  const handleScreenSelect = (screenId, isSelected) => {
    setSelectedScreens((prev) =>
      isSelected ? [...prev, screenId] : prev.filter((id) => id !== screenId)
    );
  };

  // Device commands aimed at a folder have to reach screens nested anywhere
  // beneath it, not just its immediate children.
  const getAllScreenIdsFromFolders = (folderIds) => {
    const collected = new Set();

    const walk = (folderId) => {
      screensList
        .filter((item) => parentIdOf(item) === folderId)
        .forEach((item) => collected.add(item._id));

      foldersList
        .filter((folder) => parentIdOf(folder) === folderId)
        .forEach((folder) => walk(folder._id));
    };

    folderIds.forEach(walk);
    return Array.from(collected);
  };

  const targetedScreenIds = () => {
    const ids = [...selectedScreens];
    if (selectedFolders.length > 0) {
      ids.push(...getAllScreenIdsFromFolders(selectedFolders));
    }
    return Array.from(new Set(ids));
  };

  const handleBulkCopyFolders = () => {
    if (selectedFolders.length === 0) return;
    // Copying a folder copies the screens inside it, so the screen list needs
    // a refetch too.
    dispatch(bulkCopyFolders({ folderIds: selectedFolders, siteId })).then(() =>
      dispatch(fetchScreens({ siteId }))
    );
    setSelectedFolders([]);
  };

  const handleBulkCopyScreens = () => {
    if (selectedScreens.length === 0) return;
    dispatch(bulkCopyScreens({ screenIds: selectedScreens, siteId }));
    setSelectedScreens([]);
  };

  const handleBulkDeleteFolders = () => {
    if (selectedFolders.length === 0) return;
    setBulkDeleteModal({
      type: 'folders',
      items: selectedFolders,
      isOpen: true,
    });
  };

  const handleBulkDeleteScreens = () => {
    if (selectedScreens.length === 0) return;
    setBulkDeleteModal({
      type: 'screens',
      items: selectedScreens,
      isOpen: true,
    });
  };

  const onConfirmBulkDelete = () => {
    if (bulkDeleteModal.type === 'folders') {
      dispatch(
        bulkDeleteFolders({ folderIds: bulkDeleteModal.items, siteId })
      ).then(() => dispatch(fetchScreens({ siteId })));
      setSelectedFolders([]);
    } else if (bulkDeleteModal.type === 'screens') {
      dispatch(bulkDeleteScreens({ screenIds: bulkDeleteModal.items, siteId }));
      setSelectedScreens([]);
    }
    setBulkDeleteModal({ type: null, items: [], isOpen: false });
  };

  const handleBulkUpdateScreens = async () => {
    const ids = targetedScreenIds();
    if (ids.length === 0) return;
    setDeviceInfos(await dispatch(bulkUpdate(ids)));
  };

  const handleBulkVolumeScreens = () => {
    if (targetedScreenIds().length === 0) return;
    setBulkVolumeModal({ isOpen: true, volume: 50 });
  };

  const onConfirmBulkVolume = () => {
    const ids = targetedScreenIds();
    if (ids.length > 0) dispatch(bulkVolume(ids, bulkVolumeModal.volume));
    setBulkVolumeModal({ isOpen: false, volume: 50 });
  };

  // ---------------------------------------------------------------------
  // Drag and drop
  // ---------------------------------------------------------------------
  const isDescendantFolder = (parentId, childId) => {
    if (!parentId) return false;

    const walkUp = (folderId) => {
      const folder = foldersList.find((f) => f._id === folderId);
      if (!folder) return false;

      const folderParentId = parentIdOf(folder);
      if (folderParentId === childId) return true;
      return folderParentId ? walkUp(folderParentId) : false;
    };

    return walkUp(parentId);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);

    const folder = currentFolders.find((f) => f._id === event.active.id);
    const item = currentScreens.find((s) => s._id === event.active.id);

    if (folder) setDraggedItem({ type: 'folder', item: folder });
    else if (item) setDraggedItem({ type: 'screen', item });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const draggedId = active.id;
      // FolderTree namespaces its droppable ids so they can't collide with the
      // grid's; strip the prefix back off here.
      const targetId = String(over.id).startsWith('tree-folder-')
        ? String(over.id).replace('tree-folder-', '')
        : over.id;

      const isKnownTarget =
        targetId === 'root' || foldersList.some((f) => f._id === targetId);
      const newParentId = targetId === 'root' ? null : targetId;

      if (draggedItem?.type === 'screen' && isKnownTarget) {
        dispatch(
          moveScreen({
            screenId: draggedId,
            newParentId,
            siteId,
            optimistic: true,
          })
        );
      } else if (
        draggedItem?.type === 'folder' &&
        isKnownTarget &&
        !isDescendantFolder(targetId, draggedId)
      ) {
        dispatch(moveFolder({ folderId: draggedId, newParentId, siteId }));
      }
    }

    setActiveId(null);
    setDraggedItem(null);
  };

  // ---------------------------------------------------------------------
  // Inline rename
  // ---------------------------------------------------------------------
  const startRenaming = (type, id, name) => setRenamingItem({ type, id, name });
  const cancelRenaming = () => setRenamingItem(null);

  const handleRenameSubmit = async (newName) => {
    const nameToUse = (newName || '').trim();

    if (!renamingItem || !nameToUse) {
      cancelRenaming();
      return;
    }

    try {
      if (renamingItem.type === 'folder') {
        const folder = foldersList.find((f) => f._id === renamingItem.id);
        if (folder) {
          await dispatch(
            updateFolder({
              id: renamingItem.id,
              name: nameToUse,
              parentId: parentIdOf(folder),
              siteId,
            })
          );
        }
      } else if (renamingItem.type === 'screen') {
        const target = screensList.find((s) => s._id === renamingItem.id);
        if (target) {
          // Spread the whole screen so a rename can't blank out fields the
          // form would normally send (adsum ids, screenConfigs, scheduling).
          await dispatch(
            modifyScreen({
              ...target,
              id: renamingItem.id,
              name: nameToUse,
              siteId,
              siteName: target.siteName || siteName,
            })
          );
          dispatch(fetchScreens({ siteId }));
        }
      }
    } catch (err) {
      log('Rename error:', err);
    }

    cancelRenaming();
  };

  // ---------------------------------------------------------------------
  // OTA
  // ---------------------------------------------------------------------
  const resetOtaForm = () => {
    setOtaFile(null);
    setOtaVersion('');
    setOtaDescription('');
    setSelectedBundleId('');
  };

  const handleClickOta = async (ids) => {
    if (!ids || ids.length === 0) return;

    await Promise.all([fetchLatestBundle(), fetchOtaBundles()]);

    setOtaModal({ isOpen: true, screenIds: ids });
    resetOtaForm();
  };

  const handleOtaSubmit = async () => {
    const selectedBundle = otaBundles.find(
      (item) => item.id === selectedBundleId
    );

    if (selectedBundleId && !selectedBundle) {
      Modal.error({
        title: 'Bundle Not Found',
        content: 'Please select an available older bundle.',
      });
      return;
    }

    if (!selectedBundle && !otaVersion.trim()) {
      Modal.error({
        title: 'Version Required',
        content: 'Please enter the OTA version.',
      });
      return;
    }

    if (!selectedBundle && !otaDescription.trim()) {
      Modal.error({
        title: 'Description Required',
        content: 'Please enter a description for this bundle.',
      });
      return;
    }

    if (!selectedBundle && !otaFile) {
      Modal.error({
        title: 'Bundle Required',
        content: 'Please select index.android.bundle.',
      });
      return;
    }

    try {
      setOtaUploading(true);

      let targetVersion = selectedBundle?.version || otaVersion.trim();

      if (selectedBundle) {
        // Rolling back: the bundle already exists, it just needs re-activating.
        const activateResponse = await axios.put(
          `${url}/api/ota/version/activate`,
          {
            bundleId: selectedBundle.id,
            version: selectedBundle.version,
            platform: 'android',
            forceUpdate: true,
          }
        );

        targetVersion = activateResponse?.data?.data?.version || targetVersion;
      } else {
        const formData = new FormData();
        formData.append('file', otaFile);
        formData.append('version', targetVersion);
        formData.append('description', otaDescription.trim());
        formData.append('platform', 'android');
        formData.append('forceUpdate', 'true');
        formData.append('userId', uid || '');

        await axios.put(`${url}/api/ota/version`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      const otaTargets = [...otaModal.screenIds];
      const otaResult = await dispatch(
        doOta({ screenIds: otaTargets, version: targetVersion })
      );
      const deliveredCount = (otaResult?.online || []).length;
      const offlineCount = (otaResult?.offline || []).length;

      Modal.success({
        title: selectedBundle ? 'Bundle Activated' : 'OTA Uploaded',
        content: `Version ${targetVersion} is active. Update sent to ${deliveredCount} online screen(s)${
          offlineCount
            ? `, ${offlineCount} offline screen(s) will update on next startup`
            : ''
        }.`,
      });

      setOtaModal({ isOpen: false, screenIds: [] });
      resetOtaForm();
      fetchOtaBundles();

      if (otaRecheckTimerRef.current) clearTimeout(otaRecheckTimerRef.current);
      otaRecheckTimerRef.current = setTimeout(() => {
        handleCheckScreensConnection(otaTargets);
      }, 30000);
    } catch (err) {
      console.error('OTA update error:', err);
      Modal.error({
        title: 'OTA Update Failed',
        content:
          err?.response?.data?.message ||
          err?.message ||
          'Failed to update the OTA bundle.',
      });
    } finally {
      setOtaUploading(false);
    }
  };

  const permDeleteMessage = useFormatMessage('Screens.permDelete');

  // ---------------------------------------------------------------------
  // Drag/drop + rename sub-components
  // ---------------------------------------------------------------------
  const DraggableItem = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } =
      useDraggable({ id });

    const style = transform
      ? {
          transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
          zIndex: isDragging ? 1000 : 'auto',
        }
      : undefined;

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...listeners}
        {...attributes}
        className={isDragging ? 'dragging' : ''}
      >
        {children}
      </div>
    );
  };

  const DroppableItem = ({ id, children }) => {
    const { setNodeRef, isOver } = useDroppable({ id });

    return (
      <div ref={setNodeRef} className={isOver ? 'drop-target' : ''}>
        {children}
      </div>
    );
  };

  const InlineRenameInput = ({ initialValue, onSave, onCancel }) => {
    const [value, setValue] = useState(initialValue);
    const inputRef = useRef(null);
    const isSubmittingRef = useRef(false);

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, []);

    const submit = () => {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      onSave(value.trim());
    };

    return (
      <input
        ref={inputRef}
        type="text"
        style={{
          border: '1px solid #3b82f6',
          borderRadius: '4px',
          padding: '2px 6px',
          fontSize: '14px',
          fontWeight: '500',
          color: '#1f2937',
          background: 'white',
          width: '100%',
          minWidth: '120px',
        }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          // Space would otherwise reach the card and toggle selection.
          if (e.key === ' ') e.stopPropagation();

          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            submit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            isSubmittingRef.current = true;
            onCancel();
          }
        }}
        onBlur={() => {
          // Short delay so a click that only shifts focus inside the input
          // doesn't count as committing the rename.
          setTimeout(() => {
            if (
              !isSubmittingRef.current &&
              document.activeElement !== inputRef.current
            ) {
              submit();
            }
          }, 100);
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
    );
  };

  const hasSelection = selectedFolders.length > 0 || selectedScreens.length > 0;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <section className="screens">
        {deleteModal.isOpen && (
          <ModalDeleteConfirmation
            title="Delete Screen"
            onCloseHandler={onCloseModalHandler}
            onDeleteHandler={onDeleteScreenHandler}
            description={permDeleteMessage}
            visible={deleteModal.isOpen}
          />
        )}

        <ModalDelete
          isVisible={isDeleteModal}
          onOk={handleDeleteScreenGroup}
          onCancel={handleCancelDeleteCategory}
          title="Delete Screen Group"
          subtitle={deleteGroup ? deleteGroup.name : ''}
          loading={loadingScreenGroup}
        />

        <ModalMediaCategoryForm
          visible={isModalShow}
          onCancel={() => setIsModalShow(false)}
          onCreate={onCreateScreenGroup}
          category={screenGroup}
          setCategory={(data) => dispatch(setScreenGroup(data))}
          isEditing={!!screenGroup._id}
        />

        <ModalScreenSchedulingForm
          visible={isModalSchedulingShow}
          onCancel={() => setIsModalSchedulingShow(false)}
          onCreate={onCreateScreenScheduling}
          scheduling={screenScheduling}
          setScheduling={(scheduling) =>
            dispatch(setScreenScheduling(scheduling))
          }
          isEditing={!!screenScheduling._id}
          siteId={siteId}
        />

        <ModalDelete
          isVisible={isDeleteScreenSchedulingModalShow}
          onOk={handleOkDeleteScreenScheduling}
          onCancel={handleCancelDeleteScreenScheduling}
          title="Delete Screen Scheduling"
          subtitle={deletedScreenScheduling?.name || ''}
          loading={loadingScreenScheduling}
        />

        {visible && (
          <ModalScreenForm
            visible
            onCancel={() => setVisible(false)}
            onCreate={onCreate}
            screen={screen}
            setScreen={setScreen}
            isEditing={isEditing}
          />
        )}

        {apikeyModal.isOpen && (
          <ModalConfirmation
            title={
              apikeyModal.screen?.apiKeyPreview
                ? 'Revoke API Key?'
                : 'Generate New API Key?'
            }
            onConfirmHandler={() =>
              apikeyModal.screen?.apiKeyPreview
                ? dispatch(
                    revokeApiKey({
                      screenId: apikeyModal.screen._id,
                      siteId,
                    })
                  )
                : dispatch(
                    generateApiKey(
                      { screenId: apikeyModal.screen._id, siteId },
                      onCloseModalApikeyHandler()
                    )
                  )
            }
            onCloseHandler={onCloseModalApikeyHandler}
            description={
              apikeyModal.screen.apiKeyPreview
                ? `Revoking the API key will immediately disable this screen's ability to fetch content.
Are you sure you want to proceed?`
                : `Are you sure you want to generate a new API key for this screen?`
            }
            visible={apikeyModal.isOpen}
          />
        )}

        {isFolderModalVisible && (
          <ModalFolderForm
            visible={isFolderModalVisible}
            onCancel={onCloseFolderModal}
            folder={currentFolderEdit}
            setFolder={setCurrentFolderEdit}
            isEditing={isEditingFolder}
          />
        )}

        {deleteFolderModal.isOpen && (
          <ModalDeleteConfirmation
            title="Delete Folder"
            onCloseHandler={() =>
              setDeleteFolderModal({ folder: null, isOpen: false })
            }
            onDeleteHandler={onDeleteFolderConfirm}
            description={`Are you sure you want to delete the folder "${deleteFolderModal.folder?.name}"? This action cannot be undone.`}
            visible={deleteFolderModal.isOpen}
          />
        )}

        {bulkDeleteModal.isOpen && (
          <ModalDeleteConfirmation
            title={`Delete ${bulkDeleteModal.items.length} ${
              bulkDeleteModal.type === 'folders' ? 'Folder' : 'Screen'
            }${bulkDeleteModal.items.length > 1 ? 's' : ''}`}
            onCloseHandler={() =>
              setBulkDeleteModal({ type: null, items: [], isOpen: false })
            }
            onDeleteHandler={onConfirmBulkDelete}
            description={
              bulkDeleteModal.type === 'folders'
                ? `Deleting ${bulkDeleteModal.items.length} folder(s) also deletes every screen inside them, including sub-folders. This action cannot be undone.`
                : `Are you sure you want to delete ${bulkDeleteModal.items.length} screen(s)? This action cannot be undone.`
            }
            visible={bulkDeleteModal.isOpen}
          />
        )}

        {bulkVolumeModal.isOpen && (
          <Modal
            title="Adjust Screen Volume"
            open={bulkVolumeModal.isOpen}
            onOk={onConfirmBulkVolume}
            onCancel={() => setBulkVolumeModal({ isOpen: false, volume: 50 })}
            okText="Apply Volume"
            cancelText="Cancel"
            okButtonProps={{
              style: { backgroundColor: '#64D5D2', borderColor: '#64D5D2' },
            }}
          >
            <div style={{ padding: '20px 0' }}>
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                  }}
                >
                  Volume Level:{' '}
                  <span style={{ color: '#64D5D2' }}>
                    {bulkVolumeModal.volume}%
                  </span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={bulkVolumeModal.volume}
                  onChange={(e) =>
                    setBulkVolumeModal((prev) => ({
                      ...prev,
                      volume: parseInt(e.target.value, 10),
                    }))
                  }
                  style={{ width: '100%' }}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '4px',
                    fontSize: '12px',
                    color: '#666',
                  }}
                >
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
              <div
                style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}
              >
                This will adjust the volume for all selected screens and screens
                within selected folders ({targetedScreenIds().length} screen(s)).
              </div>
            </div>
          </Modal>
        )}

        <Modal
          open={deviceLogModal}
          title={
            <div className="device-log-modal-title">
              <span>
                {`Device Logs - ${
                  screensList.find(
                    (item) => String(item._id) === String(selectedScreenId)
                  )?.name || selectedScreenId
                }`}
              </span>
              <button
                type="button"
                className="button-secondary"
                onClick={() =>
                  selectedScreenId && fetchDeviceLogsFor(selectedScreenId)
                }
                disabled={deviceLogLoading}
              >
                <i className="feather-refresh-cw" />
                <span>Refresh</span>
              </button>
            </div>
          }
          width={1000}
          footer={null}
          onCancel={() => {
            setDeviceLogModal(false);
            setDeviceLogs([]);
          }}
        >
          <Table
            rowKey="_id"
            loading={deviceLogLoading}
            dataSource={deviceLogs}
            size="small"
            bordered
            scroll={{ y: 500 }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: [20, 50, 100],
              showTotal: (total) => `${total} logs`,
            }}
            columns={[
              {
                title: 'Time',
                dataIndex: 'createdAt',
                width: 180,
                sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
                defaultSortOrder: 'descend',
                render: (value) => moment(value).format('DD/MM/YYYY HH:mm:ss'),
              },
              {
                title: 'Event',
                dataIndex: 'event',
                width: 120,
                render: (value) => (
                  <Tag color={LOG_EVENT_COLORS[value] || 'blue'}>{value}</Tag>
                ),
              },
              {
                title: 'Status',
                dataIndex: 'status',
                width: 100,
                render: (value) => (
                  <Tag color={LOG_STATUS_COLORS[value] || 'default'}>
                    {value}
                  </Tag>
                ),
              },
              {
                title: 'App Version',
                dataIndex: 'appVersion',
                width: 120,
                render: (value) => value || '-',
              },
              {
                title: 'Message',
                dataIndex: 'message',
                ellipsis: true,
                render: (value) => (
                  <Text ellipsis={{ tooltip: value }}>{value || '-'}</Text>
                ),
              },
            ]}
            expandable={{
              expandedRowRender: (record) => (
                <>
                  <Divider orientation="left">Detail</Divider>

                  <p>
                    <b>Message</b>
                  </p>

                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      background: '#fafafa',
                      padding: 12,
                      borderRadius: 6,
                    }}
                  >
                    {record.message || '-'}
                  </pre>

                  {record.data && (
                    <>
                      <Divider orientation="left">Data</Divider>

                      <pre
                        style={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          background: '#fafafa',
                          padding: 12,
                          borderRadius: 6,
                          maxHeight: 300,
                          overflow: 'auto',
                        }}
                      >
                        {JSON.stringify(record.data, null, 2)}
                      </pre>
                    </>
                  )}
                </>
              ),
              rowExpandable: (record) => !!record.data || !!record.message,
            }}
          />
        </Modal>

        <div className="file-explorer-section">
          <div className="explorer-header">
            {/* Matches the reference: the breadcrumb is available but not
                mounted, because .explorer-header is a centred flex row sized
                for a single child. Re-enable together with the
                .explorer-navigation rules if you want it. */}
            {/* <div className="explorer-navigation">
              <FolderBreadcrumb
                currentFolder={currentFolder}
                folders={foldersList || []}
                onNavigate={handleFolderNavigate}
                showRoot
              />
            </div> */}

            <div className="explorer-controls">
              <div className="explorer-primary-row">
                <div className="explorer-search">
                  <div className="search-input-wrapper">
                    <i className="feather-search" />
                    <input
                      type="text"
                      placeholder="Search folders and screens..."
                      value={search}
                      onChange={handleSearch}
                    />
                  </div>
                  {searchError && (
                    <div className="search-error">{searchError}</div>
                  )}
                </div>

                <div className="explorer-filters">
                  <div className="explorer-filter">
                    <div
                      className="screen-group-dropdown"
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                    >
                      <div className="dropdown-selection">
                        <ForwardIcon />
                        <span>
                          {selectedGroup === 'all'
                            ? 'All Screen Groups'
                            : getCategory(selectedGroup)}
                        </span>
                      </div>
                      <i
                        className={`feather-chevron-down ${
                          dropdownOpen ? 'dropdown-open' : 'dropdown-closed'
                        }`}
                      />

                      <CategoryDropdown
                        categories={screenGroupList}
                        setTempSelectedCategory={(id) =>
                          setTempSelectedGroup(id)
                        }
                        handleEditCategory={
                          role === 'superadmin'
                            ? (cat) => handleEditCategory(cat._id)
                            : null
                        }
                        handleDeleteCategory={
                          role === 'superadmin'
                            ? (cat) => handleDeleteCategory(cat)
                            : null
                        }
                        modalOpen={dropdownOpen}
                        handleSetPlaylist={handleSetPlaylist}
                        title="Screen Group"
                        tempSelectedCategory={tempSelectedGroup}
                        handleNew={
                          role === 'superadmin' ? handleAddCategory : null
                        }
                        filter
                      />
                    </div>
                  </div>

                  <div className="explorer-filter">
                    <DropdownWrapper
                      setIsOpen={setSchedulingDropdownOpen}
                      isOpen={schedulingDropdownOpen}
                      label="Screen Scheduling"
                      getItemLabel={getScreenScheduling}
                      selectedItem={selectedScreenScheduling}
                    >
                      <CategoryDropdown
                        categories={screenSchedulingList}
                        title="Screen Scheduling"
                        setTempSelectedCategory={(id) =>
                          setTempSelectedScreenScheduling(id)
                        }
                        handleEditCategory={(cat) =>
                          handleEditScreenScheduling(cat._id)
                        }
                        handleDeleteCategory={(cat) =>
                          handleDeleteScreenScheduling(cat)
                        }
                        modalOpen={schedulingDropdownOpen}
                        handleSetPlaylist={handleSetScreenScheduling}
                        tempSelectedCategory={tempSelectedScreenScheduling}
                        handleNew={handleAddScreenScheduling}
                        filter
                      />
                    </DropdownWrapper>
                  </div>

                  <div className="explorer-filter">
                    <select
                      className="app-version-filter"
                      value={selectedAppVersion}
                      onChange={(event) => {
                        setSelectedAppVersion(event.target.value);
                        setSelectedScreens([]);
                      }}
                      aria-label="Filter screens by app version"
                      title="Filter screens by app version"
                      disabled={otaBundlesLoading}
                    >
                      <option value="all">
                        {otaBundlesLoading
                          ? 'Loading App Versions...'
                          : 'All App Versions'}
                      </option>
                      {appVersionOptions.map((version) => (
                        <option key={version} value={version}>
                          {`App Version ${version}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="explorer-actions explorer-create-actions">
                  <div
                    className="device-connection-summary"
                    title={
                      lastCheckedAt
                        ? `Last checked ${moment(lastCheckedAt).format(
                            'DD/MM/YYYY HH:mm:ss'
                          )}`
                        : 'Not checked yet'
                    }
                  >
                    <span className="summary-online">
                      <span className="device-status-dot" />
                      {connectionSummary.online} Online
                    </span>
                    <span className="summary-offline">
                      <span className="device-status-dot" />
                      {connectionSummary.offline} Offline
                    </span>
                  </div>
                  <button
                    className="button-secondary"
                    onClick={() => handleCheckScreensConnection(screenIds)}
                    disabled={screenIds.length === 0 || checkingScreenIds.length > 0}
                    title="Check connection of every screen in this folder"
                  >
                    <i
                      className={`feather-wifi ${
                        checkingScreenIds.length > 0 ? 'is-spinning' : ''
                      }`}
                    />
                    <span>
                      {checkingScreenIds.length > 0
                        ? 'Checking...'
                        : 'Refresh Status'}
                    </span>
                  </button>
                  <button
                    className="button-secondary"
                    onClick={handleCreateFolder}
                  >
                    <i className="feather-folder-plus" />
                    <span>New Folder</span>
                  </button>
                  <button
                    className="button-primary"
                    onClick={onNewScreenHandler}
                  >
                    <i className="feather-plus" />
                    <span>Add Screen</span>
                  </button>
                </div>
              </div>

              {hasSelection && (
                <div className="explorer-selection-row">
                  <div className="selection-summary" aria-live="polite">
                    <i className="feather-check-square" />
                    <span>
                      {selectedFolders.length > 0 &&
                        `${selectedFolders.length} folder${
                          selectedFolders.length > 1 ? 's' : ''
                        }`}
                      {selectedFolders.length > 0 &&
                        selectedScreens.length > 0 &&
                        ', '}
                      {selectedScreens.length > 0 &&
                        `${selectedScreens.length} screen${
                          selectedScreens.length > 1 ? 's' : ''
                        }`}{' '}
                      selected
                    </span>
                  </div>

                  <div className="selection-action-groups">
                    {selectedScreens.length > 0 && (
                      <div className="explorer-actions explorer-device-actions">
                        <button
                          className="button-secondary"
                          onClick={() =>
                            handleCheckScreensConnection(selectedScreens)
                          }
                          title="Check selected screens connection"
                        >
                          <i className="feather-wifi" />
                          <span>Check Connection</span>
                        </button>

                        <button
                          className="button-secondary"
                          onClick={() => openDeviceLogs(selectedScreens[0])}
                          disabled={selectedScreens.length !== 1}
                          title={
                            selectedScreens.length === 1
                              ? 'Open device logs'
                              : 'Select one screen to view logs'
                          }
                        >
                          <i className="feather-file-text" />
                          <span>Logs</span>
                        </button>

                        <button
                          className="button-secondary"
                          onClick={() => handleClickOta(selectedScreens)}
                          title="Update selected screens over the air"
                        >
                          <i className="feather-refresh-cw" />
                          <span>OTA</span>
                        </button>
                      </div>
                    )}

                    <div className="explorer-actions bulk-actions">
                      {selectedFolders.length > 0 && (
                        <>
                          <button
                            className="button-secondary"
                            onClick={handleBulkCopyFolders}
                          >
                            <i className="feather-copy" />
                            <span>
                              Copy {selectedFolders.length} Folder
                              {selectedFolders.length > 1 ? 's' : ''}
                            </span>
                          </button>
                          <button
                            className="button-secondary"
                            onClick={handleBulkUpdateScreens}
                          >
                            <i className="feather-download-cloud" />
                            <span>Update Screens</span>
                          </button>
                          <button
                            className="button-secondary"
                            onClick={handleBulkVolumeScreens}
                          >
                            <i className="feather-volume-2" />
                            <span>Adjust Volume</span>
                          </button>
                          <button
                            className="button-secondary delete-btn"
                            onClick={handleBulkDeleteFolders}
                          >
                            <i className="feather-trash" />
                            <span>
                              Delete {selectedFolders.length} Folder
                              {selectedFolders.length > 1 ? 's' : ''}
                            </span>
                          </button>
                        </>
                      )}

                      {selectedScreens.length > 0 && (
                        <>
                          <button
                            className="button-secondary"
                            onClick={handleBulkCopyScreens}
                          >
                            <i className="feather-copy" />
                            <span>
                              Copy {selectedScreens.length} Screen
                              {selectedScreens.length > 1 ? 's' : ''}
                            </span>
                          </button>
                          <button
                            className="button-secondary"
                            onClick={handleBulkUpdateScreens}
                          >
                            <i className="feather-download-cloud" />
                            <span>
                              Update {selectedScreens.length} Screen
                              {selectedScreens.length > 1 ? 's' : ''}
                            </span>
                          </button>
                          <button
                            className="button-secondary"
                            onClick={handleBulkVolumeScreens}
                          >
                            <i className="feather-volume-2" />
                            <span>Adjust Volume</span>
                          </button>
                          <button
                            className="button-secondary delete-btn"
                            onClick={handleBulkDeleteScreens}
                          >
                            <i className="feather-trash" />
                            <span>
                              Delete {selectedScreens.length} Screen
                              {selectedScreens.length > 1 ? 's' : ''}
                            </span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="explorer-main">
            <div className="explorer-sidebar">
              <div className="sidebar-header">
                <h4>Folders</h4>
              </div>
              <div className="sidebar-content">
                <FolderTree
                  folders={foldersList}
                  screens={screensList}
                  currentFolder={currentFolder}
                  onFolderSelect={handleFolderNavigate}
                  enableDragDrop
                  showRoot
                />
              </div>
            </div>

            <div className="explorer-content">
              {loading ? (
                <div className="empty-folder">
                  <ClipLoader size={32} color="#64D5D2" />
                </div>
              ) : appVersionFilterLoading && selectedAppVersion !== 'all' ? (
                <div className="empty-folder">
                  <ClipLoader size={32} color="#64D5D2" />
                  <p>Checking device last installed versions...</p>
                </div>
              ) : currentFolders.length === 0 && currentScreens.length === 0 ? (
                <div className="empty-folder">
                  <i
                    className="feather-folder"
                    style={{ fontSize: '48px', color: '#ccc' }}
                  />
                  <p>
                    {search ||
                    selectedAppVersion !== 'all' ||
                    selectedGroup !== 'all' ||
                    selectedScreenScheduling !== 'all'
                      ? 'No folders or screens match the current filters.'
                      : 'This folder is empty'}
                  </p>
                  <button
                    className="button-secondary"
                    onClick={handleCreateFolder}
                  >
                    Create New Folder
                  </button>
                </div>
              ) : (
                <div className="explorer-grid">
                  {currentFolders.map((folder) => (
                    <DroppableItem key={`folder-${folder._id}`} id={folder._id}>
                      <DraggableItem id={folder._id}>
                        <div
                          className={`explorer-item folder-item ${
                            selectedFolders.includes(folder._id)
                              ? 'selected'
                              : ''
                          }`}
                          onDoubleClick={() => handleFolderNavigate(folder._id)}
                        >
                          <div className="item-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedFolders.includes(folder._id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleFolderSelect(
                                  folder._id,
                                  e.target.checked
                                );
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="item-icon">
                            <i className="feather-folder" />
                          </div>
                          <div className="item-details">
                            <h4
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                startRenaming(
                                  'folder',
                                  folder._id,
                                  folder.name
                                );
                              }}
                            >
                              {renamingItem?.type === 'folder' &&
                              renamingItem?.id === folder._id ? (
                                <InlineRenameInput
                                  initialValue={folder.name}
                                  onSave={(newName) =>
                                    newName && newName !== folder.name
                                      ? handleRenameSubmit(newName)
                                      : cancelRenaming()
                                  }
                                  onCancel={cancelRenaming}
                                />
                              ) : (
                                folder.name
                              )}
                            </h4>
                            <p>
                              {moment(folder.createdAt).format('DD MMM YYYY')}
                            </p>
                          </div>
                          <div className="item-actions">
                            <button
                              className="action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditFolder(folder);
                              }}
                              title="Edit Folder"
                            >
                              <i className="feather-edit" />
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteFolderModal({ folder, isOpen: true });
                              }}
                              title="Delete Folder"
                            >
                              <i className="feather-trash" />
                            </button>
                          </div>
                        </div>
                      </DraggableItem>
                    </DroppableItem>
                  ))}

                  {currentScreens.map((item) => {
                    const deviceStatus = getDeviceStatus(item._id);
                    const statusMeta =
                      DEVICE_STATUS_META[deviceStatus] ||
                      DEVICE_STATUS_META.unknown;
                    const lastInstalledAppVersion =
                      deviceLastInstalledVersions[String(item._id)];

                    return (
                      <DroppableItem key={`screen-${item._id}`} id={item._id}>
                        <DraggableItem id={item._id}>
                          <div
                            className={`explorer-item screen-item device-${deviceStatus} ${
                              selectedScreens.includes(item._id)
                                ? 'selected'
                                : ''
                            }`}
                            onDoubleClick={() => handleGoTo(item)}
                          >
                            <div className="item-checkbox">
                              <input
                                type="checkbox"
                                checked={selectedScreens.includes(item._id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleScreenSelect(item._id, e.target.checked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            <div className="item-icon">
                              <i
                                className="feather-monitor"
                                style={{ color: statusMeta.color }}
                              />
                            </div>
                            <div
                              className={`device-status-badge device-status-${deviceStatus}`}
                              title={
                                lastCheckedAt
                                  ? `Last checked ${moment(lastCheckedAt).format(
                                      'DD/MM/YYYY HH:mm:ss'
                                    )}`
                                  : statusMeta.label
                              }
                            >
                              <span className="device-status-dot" />
                              <span>{statusMeta.label}</span>
                            </div>
                            <div className="item-details">
                              <h4
                                onDoubleClick={(e) => {
                                  e.stopPropagation();
                                  startRenaming('screen', item._id, item.name);
                                }}
                              >
                                {renamingItem?.type === 'screen' &&
                                renamingItem?.id === item._id ? (
                                  <InlineRenameInput
                                    initialValue={item.name}
                                    onSave={(newName) =>
                                      newName && newName !== item.name
                                        ? handleRenameSubmit(newName)
                                        : cancelRenaming()
                                    }
                                    onCancel={cancelRenaming}
                                  />
                                ) : (
                                  item.name
                                )}
                              </h4>
                              <p>{item.description || 'No description'}</p>
                              <p
                                className="screen-app-version"
                                style={{
                                  marginTop: 4,
                                  color: '#6b7280',
                                  fontSize: 12,
                                }}
                              >
                                App Version:{' '}
                                <strong style={{ color: '#374151' }}>
                                  {lastInstalledAppVersion ||
                                    (appVersionFilterLoading
                                      ? 'Checking...'
                                      : 'Not found')}
                                </strong>
                              </p>
                            </div>
                            <div className="item-actions">
                              <button
                                className="action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCheckScreensConnection([item._id]);
                                }}
                                disabled={deviceStatus === 'checking'}
                                title="Check Connection"
                              >
                                <i className="feather-wifi" />
                              </button>
                              <button
                                className="action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeviceLogs(item._id);
                                }}
                                title="Device Logs"
                              >
                                <i className="feather-file-text" />
                              </button>
                              <button
                                className="action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClickOta([item._id]);
                                }}
                                title="OTA Update"
                              >
                                <i className="feather-refresh-cw" />
                              </button>
                              <button
                                className="action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePreviewScreen(item);
                                }}
                                title="Preview Current Content"
                              >
                                <i className="feather-eye" />
                              </button>
                              <button
                                className="action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setScreen(item);
                                  setVisible(true);
                                  setIsEditing(true);
                                }}
                                title="Edit Screen"
                              >
                                <i className="feather-edit" />
                              </button>
                              <button
                                className="action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setApikeyModal({
                                    screen: item,
                                    isOpen: true,
                                  });
                                }}
                                title={
                                  item.apiKeyPreview
                                    ? 'Revoke API Key'
                                    : 'Generate API Key'
                                }
                              >
                                <i
                                  className={
                                    item.apiKeyPreview
                                      ? 'feather-x-circle'
                                      : 'feather-unlock'
                                  }
                                />
                              </button>
                              <button
                                className="action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  dispatch(
                                    downloadScreenConfig({
                                      screenId: item._id,
                                      siteName,
                                      screenName: item.name,
                                    })
                                  );
                                }}
                                title="Download Screen Config"
                              >
                                <i className="feather-download" />
                              </button>
                              <button
                                className="action-btn delete-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRemoveButtonClickHandler(item._id);
                                }}
                                title="Delete Screen"
                              >
                                <i className="feather-trash" />
                              </button>
                            </div>
                          </div>
                        </DraggableItem>
                      </DroppableItem>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {error && <div className="error-message">Error: {error}</div>}

        {isPreviewModalOpen && previewScreen && (
          <Modal
            title="Screen Preview"
            open={isPreviewModalOpen}
            onCancel={closePreviewModal}
            footer={null}
            width={1200}
            style={{ top: 20 }}
            className="screen-preview-modal"
          >
            <div style={{ marginBottom: '24px' }}>
              <h2
                style={{
                  color: '#4A90E2',
                  fontSize: '24px',
                  fontWeight: '600',
                  margin: '0 0 16px 0',
                }}
              >
                {previewScreen.name}
              </h2>

              {previewScreen.description && (
                <p
                  style={{
                    color: '#666',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    margin: '0 0 20px 0',
                  }}
                >
                  {previewScreen.description}
                </p>
              )}

              <div style={{ display: 'flex', gap: '24px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ScreenTitleIcon size={20} color="#64D5D2" />
                  <span style={{ color: '#666', fontSize: '14px' }}>
                    {previewScreen.width && previewScreen.height
                      ? `${previewScreen.width} x ${previewScreen.height}`
                      : 'Resolution not set'}
                  </span>
                </div>
              </div>

              {previewData ? (
                <div style={{ marginTop: '24px' }}>
                  <h3
                    style={{
                      color: '#4A90E2',
                      fontSize: '18px',
                      fontWeight: '600',
                      margin: '0 0 16px 0',
                    }}
                  >
                    Current Content:
                  </h3>

                  <div
                    style={{
                      border: '1px solid #e1e5e9',
                      borderRadius: '8px',
                      padding: '16px',
                      backgroundColor: '#f8f9fa',
                    }}
                  >
                    {previewData.data && previewData.data.template ? (
                      <div>
                        <h4
                          style={{
                            color: '#333',
                            fontSize: '16px',
                            fontWeight: '500',
                            margin: '0 0 12px 0',
                          }}
                        >
                          {previewData.data.template.name}
                        </h4>

                        <div
                          style={{
                            fontSize: '12px',
                            color: '#666',
                            marginBottom: '16px',
                          }}
                        >
                          {previewData.data.template.start &&
                            previewData.data.template.end && (
                              <>
                                {moment
                                  .unix(previewData.data.template.start)
                                  .format('DD MMM YYYY')}{' '}
                                -{' '}
                                {moment
                                  .unix(previewData.data.template.end)
                                  .format('DD MMM YYYY')}
                                <span style={{ margin: '0 8px' }}>&bull;</span>
                              </>
                            )}
                          {previewData.data.totalSlots} total slots
                        </div>

                        {previewData.data.slots &&
                          previewData.data.slots.length > 0 && (
                            <TimelineSequence
                              duration={50}
                              initialSlots={previewData.data.slots.map(
                                (slot) => ({
                                  ...slot,
                                  // TimelineSequence reads `medias`; the
                                  // preview endpoint returns `activeMedia`.
                                  medias: slot.activeMedia || [],
                                })
                              )}
                              onSlotsChange={() => {}}
                              isReadOnly
                            />
                          )}
                      </div>
                    ) : (
                      <div
                        style={{
                          textAlign: 'center',
                          color: '#666',
                          padding: '20px',
                          fontStyle: 'italic',
                        }}
                      >
                        No active content scheduled for this screen
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    color: '#666',
                    padding: '40px',
                    fontStyle: 'italic',
                  }}
                >
                  Loading preview data...
                </div>
              )}
            </div>
          </Modal>
        )}

        <Modal
          title="Android OTA Update"
          open={otaModal.isOpen}
          onCancel={() => {
            if (otaUploading) return;
            setOtaModal({ isOpen: false, screenIds: [] });
            resetOtaForm();
          }}
          onOk={handleOtaSubmit}
          okText={
            otaUploading
              ? 'Processing...'
              : selectedBundleId
              ? 'Activate & Update'
              : 'Upload & Update'
          }
          cancelText="Cancel"
          confirmLoading={otaUploading}
          okButtonProps={{
            disabled:
              otaUploading ||
              otaBundlesLoading ||
              (!selectedBundleId &&
                (!otaFile || !otaVersion.trim() || !otaDescription.trim())),
          }}
        >
          <div style={{ marginBottom: 20 }}>
            <label
              style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}
            >
              Select Older Bundle
            </label>

            <select
              value={selectedBundleId}
              onChange={(event) => {
                const bundleId = event.target.value;
                const bundle = otaBundles.find((i) => i.id === bundleId);

                setSelectedBundleId(bundleId);
                setOtaFile(null);
                setOtaVersion(bundle?.version || '');
                setOtaDescription(bundle?.description || '');
              }}
              disabled={otaUploading || otaBundlesLoading}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
              }}
            >
              <option value="">
                {otaBundlesLoading
                  ? 'Loading bundles...'
                  : 'Upload a new bundle'}
              </option>

              {otaBundles
                .filter((item) => !item.active)
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {`v${item.version || '-'} - ${
                      item.description || item.name || 'No description'
                    } - ${moment(item.createdAt).format('DD/MM/YYYY')}`}
                  </option>
                ))}
            </select>

            <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
              Leave this as &ldquo;Upload a new bundle&rdquo; to publish a new
              version.
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label
              style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}
            >
              Version
            </label>

            <input
              type="text"
              value={otaVersion}
              onChange={(e) => setOtaVersion(e.target.value)}
              placeholder="Example: 1.0.11"
              disabled={otaUploading || !!selectedBundleId}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                fontSize: 14,
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label
              style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}
            >
              Description
            </label>

            <textarea
              value={otaDescription}
              onChange={(e) => setOtaDescription(e.target.value)}
              placeholder="Example: Fix screensaver restart and improve media cache"
              disabled={otaUploading || !!selectedBundleId}
              maxLength={1000}
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                fontSize: 14,
                resize: 'vertical',
              }}
            />

            <div
              style={{
                marginTop: 4,
                textAlign: 'right',
                fontSize: 12,
                color: '#999',
              }}
            >
              {otaDescription.length}/1000
            </div>
          </div>

          <div
            style={{
              marginBottom: 16,
              padding: 12,
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: 6,
            }}
          >
            <strong>Latest Uploaded Bundle</strong>

            {latestBundleLoading ? (
              <div style={{ marginTop: 8 }}>Loading...</div>
            ) : latestBundle ? (
              <>
                <div>
                  Version : {latestBundle.version || latestBundle.description}
                </div>
                {latestBundle.version && (
                  <div>Description : {latestBundle.description || '-'}</div>
                )}
                <div>
                  Uploaded :{' '}
                  {moment(latestBundle.createdAt).format('DD/MM/YYYY HH:mm:ss')}
                </div>
              </>
            ) : (
              <div>No bundle uploaded.</div>
            )}
          </div>

          <div>
            <label
              style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}
            >
              Android Bundle
            </label>

            <input
              type="file"
              accept=".bundle"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) setOtaFile(file);
              }}
              disabled={otaUploading || !!selectedBundleId}
            />

            {otaFile && (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  background: '#f5f5f5',
                  borderRadius: 6,
                }}
              >
                <strong>Selected:</strong> {otaFile.name}
                <br />
                <span>Size: {(otaFile.size / 1024 / 1024).toFixed(2)} MB</span>
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: 20,
              padding: 12,
              background: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            {selectedBundleId
              ? 'The selected older bundle will be activated before the OTA notification is sent.'
              : 'The selected screens will receive the OTA update after the bundle has been uploaded successfully.'}
          </div>
        </Modal>
      </section>

      <DragOverlay>
        {activeId && draggedItem ? (
          <div className="drag-overlay">
            <i
              className={
                draggedItem.type === 'folder'
                  ? 'feather-folder'
                  : 'feather-monitor'
              }
            />
            {draggedItem.item.name}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
